import React from 'react';
import CloseIcon from '@mui/icons-material/Close';

const MessageModal = ({ 
  isOpen, 
  onClose, 
  type, 
  message, 
  schedulingResult, 
  onConfirm,
  // Swap Optimization props
  showSwapButton = false,
  swapType = null,
  onSwapOptimize = null,
  swapLoading = false
}) => {
  if (!isOpen) return null;

  // Debug logging
  if (type === 'schedulingResult' && schedulingResult) {
    console.log('MessageModal schedulingResult:', schedulingResult);
  }

  const renderIndividualPreview = () => {
    if (!schedulingResult) {
      return (
        <div className="mt-4 p-4 bg-yellow-50 text-yellow-700 rounded-md">
          No scheduling result available. Please try again.
        </div>
      );
    }

    // Check if we have at least some data to display
    const hasData = schedulingResult.guestName || schedulingResult.date || 
                    schedulingResult.treatment || schedulingResult.therapist || 
                    schedulingResult.room;

    if (!hasData) {
      return (
        <div className="mt-4">
          <div className="p-4 bg-yellow-50 text-yellow-700 rounded-md">
            <p className="font-semibold">Unable to display booking details</p>
            <p className="text-sm mt-2">Data received but format is unexpected. Please check the console for details.</p>
            <div className="mt-2 text-xs bg-white p-2 rounded font-mono overflow-auto max-h-40">
              {JSON.stringify(schedulingResult, null, 2)}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="mt-6">
        {/* Guest Info Header */}
        {schedulingResult.guestName && (
          <div className="flex items-center gap-3 p-4 rounded-lg mb-4" style={{ backgroundColor: '#FFFBF0', border: '2px solid #E4C984' }}>
            <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: '#E4C984' }}>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="#171717" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <div className="flex-1">
              <div className="text-xl font-bold text-gray-800">
                {schedulingResult.guestName}
              </div>
              {schedulingResult.guestId && (
                <div className="text-sm text-gray-600 mt-0.5">ID: {schedulingResult.guestId}</div>
              )}
            </div>
          </div>
        )}

        {/* Booking Details Card */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden" style={{ border: '2px solid #E4C984' }}>
          <div className="px-4 py-3" style={{ backgroundColor: '#FFFBF0', borderBottom: '1px solid #E4C984' }}>
            <h3 className="text-sm font-semibold uppercase tracking-wide" style={{ color: '#171717' }}>Booking Details</h3>
          </div>
          
          <div className="p-4 bg-gray-50">
            <div className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm">
              {/* Treatment */}
              {schedulingResult.treatment && (
                <div>
                  <span className="font-semibold text-gray-600 uppercase text-xs">Treatment:</span>
                  <div className="text-gray-900 font-medium mt-0.5">{schedulingResult.treatment}</div>
                </div>
              )}

              {/* Date */}
              {schedulingResult.date && (
                <div>
                  <span className="font-semibold text-gray-600 uppercase text-xs">Date:</span>
                  <div className="text-gray-900 font-medium mt-0.5">{schedulingResult.date}</div>
                </div>
              )}

              {/* Time */}
              {schedulingResult.startTime && schedulingResult.endTime && (
                <div>
                  <span className="font-semibold text-gray-600 uppercase text-xs">Time:</span>
                  <div className="text-gray-900 font-medium mt-0.5">
                    {schedulingResult.startTime} - {schedulingResult.endTime}
                  </div>
                  <div className="text-xs text-gray-500 italic mt-0.5">(includes preparation time)</div>
                </div>
              )}

              {/* Therapist(s) - Support for multiple therapists */}
              {schedulingResult.therapist && (
                <div>
                  <span className="font-semibold text-gray-600 uppercase text-xs">
                    Therapist{schedulingResult.therapistsCount > 1 ? 's' : ''}:
                  </span>
                  <div className="text-gray-900 font-medium mt-0.5">
                    {(() => {
                      const therapists = schedulingResult.therapists || [];
                      const therapistsCount = schedulingResult.therapistsCount || 1;
                      if (therapistsCount > 1) {
                        const allNames = therapists.map(t => t.name).join(', ');
                        return `${allNames} (${therapistsCount} therapists)`;
                      }
                      return schedulingResult.therapist;
                    })()}
                  </div>
                </div>
              )}

              {/* Room */}
              {schedulingResult.room && (
                <div>
                  <span className="font-semibold text-gray-600 uppercase text-xs">Room:</span>
                  <div className="text-gray-900 font-medium mt-0.5">{schedulingResult.room}</div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Instructions */}
        {(schedulingResult.generalInstruction || schedulingResult.treatmentInstruction) && (
          <div className="mt-4 rounded-lg p-4 border" style={{ backgroundColor: '#FFFBF0', borderColor: '#E4C984' }}>
            <div className="text-sm font-semibold mb-3" style={{ color: '#171717' }}>📋 Instructions</div>
            {schedulingResult.generalInstruction && (
              <div className="mb-3">
                <div className="text-xs font-semibold text-gray-600 uppercase mb-1">General:</div>
                <div className="text-sm text-gray-800">{schedulingResult.generalInstruction}</div>
              </div>
            )}
            {schedulingResult.treatmentInstruction && (
              <div>
                <div className="text-xs font-semibold text-gray-600 uppercase mb-1">Treatment:</div>
                <div className="text-sm text-gray-800">{schedulingResult.treatmentInstruction}</div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderBulkPreview = () => {
    if (!schedulingResult || !schedulingResult.proposedSchedule) return null;
    const firstSlot = schedulingResult.proposedSchedule[0];

    return (
      <div className="mt-4 space-y-4">
        {/* Guest Info Header */}
        <div className="bg-gray-50 p-4 rounded-md">
          <div className="grid grid-cols-2 gap-4">
            <div><strong>Guest:</strong> {firstSlot.guestName}</div>
            <div><strong>Guest ID:</strong> {firstSlot.guestId}</div>
          </div>
        </div>

        {/* Slots Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Treatment</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Therapist</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time Slot</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {schedulingResult.proposedSchedule.map((slot, index) => (
                <tr key={index} className={slot.status === 'available' ? 'bg-white' : 'bg-red-50'}>
                  <td className="px-4 py-2 text-sm">{slot.date}</td>
                  <td className="px-4 py-2 text-sm">{slot.treatment}</td>
                  <td className="px-4 py-2 text-sm">{slot.therapist}</td>
                  <td className="px-4 py-2 text-sm">{slot.startTime} - {slot.endTime}</td>
                  <td className="px-4 py-2 text-sm">
                    {slot.status === 'available' ? (
                      <span className="text-green-600">Available</span>
                    ) : (
                      <span className="text-red-600">Not Available</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {schedulingResult.failedSlots && schedulingResult.failedSlots.length > 0 && (
          <div className="mt-4 bg-red-50 p-4 rounded-md">
            <h4 className="font-semibold text-red-700">Unavailable Slots:</h4>
            <ul className="mt-2 list-disc pl-5 text-red-600">
              {schedulingResult.failedSlots.map((slot, index) => (
                <li key={index}>
                  {slot.treatment} on {slot.date}: {slot.reason}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-start gap-3">
            <h2 className="text-xl font-bold flex-1 min-w-0 pr-2">
              {type === 'error' ? 'Error' : 
               type === 'success' ? 'Success' :
               type === 'schedulingResult' ? 
                (schedulingResult?.proposedSchedule ? 'Package Schedule Preview' : 'Available Slot Found') : 
               'Message'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <CloseIcon />
            </button>
          </div>

          {message && (
            <div className={`mt-4 whitespace-pre-line ${
              type === 'error' ? 'text-red-600' : 
              type === 'success' ? 'text-green-700 bg-green-50 p-4 rounded-md' : 
              'text-gray-600'
            }`}>
              {message}
            </div>
          )}

          {type === 'schedulingResult' && (
            schedulingResult?.proposedSchedule ? renderBulkPreview() : renderIndividualPreview()
          )}

          <div className="mt-6 flex justify-end space-x-4">
            {type === 'success' ? (
              <button
                onClick={onClose}
                className="px-5 py-2.5 rounded-md font-medium transition-colors"
                style={{ backgroundColor: '#E4C984', color: '#171717' }}
              >
                OK
              </button>
            ) : (
              <>
                <button
                  onClick={onClose}
                  className="px-5 py-2.5 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                
                {/* Swap Optimization Button */}
                {type === 'error' && showSwapButton && onSwapOptimize && (
                  <button
                    onClick={onSwapOptimize}
                    disabled={swapLoading}
                    className="px-5 py-2.5 rounded-md font-medium transition-colors"
                    style={{ 
                      backgroundColor: swapLoading ? '#D1D5DB' : '#F59E0B', 
                      color: '#FFFFFF',
                      cursor: swapLoading ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {swapLoading ? 'Optimizing...' : `Retry - Swap Optimize ${swapType === 'staff' ? 'Staff' : 'Cabin'}`}
                  </button>
                )}
                
                {type === 'schedulingResult' && (
                  <button
                    onClick={onConfirm}
                    className="px-5 py-2.5 rounded-md font-medium transition-colors"
                    style={{ backgroundColor: '#E4C984', color: '#171717' }}
                  >
                    Confirm Booking
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessageModal;
